import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCookieConsent } from '../context/CookieConsentContext';
import './ActivationPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ActivationPage = () => {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { preferences, setShowBanner } = useCookieConsent();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [linkData, setLinkData] = useState(null);

    // Form state
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [companyWebsite, setCompanyWebsite] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Payment cancelled message
    const paymentCancelled = searchParams.get('payment') === 'cancelled';

    useEffect(() => {
        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/activate/${token}`);
            const data = res.data.data;
            setLinkData(data);

            // Pre-fill form fields
            setContactName(data.accountSetupData?.contactName || data.contactName || '');
            setContactEmail(data.accountSetupData?.contactEmail || data.contactEmail || '');
            setPhone(data.accountSetupData?.phone || data.phone || '');
            setCompanyWebsite(data.accountSetupData?.companyWebsite || data.companyWebsite || '');
        } catch (err) {
            const errorData = err.response?.data;
            setError({
                type: errorData?.errorType || 'unknown',
                message: errorData?.message || 'Something went wrong. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    // Password strength calculation
    const getPasswordStrength = (pwd) => {
        if (!pwd) return { score: 0, label: '', color: '' };
        let score = 0;
        if (pwd.length >= 8) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/[a-z]/.test(pwd)) score++;
        if (/\d/.test(pwd)) score++;
        if (/[^A-Za-z0-9]/.test(pwd)) score++;

        const levels = [
            { score: 0, label: '', color: '' },
            { score: 1, label: 'Very Weak', color: '#ef4444' },
            { score: 2, label: 'Weak', color: '#f97316' },
            { score: 3, label: 'Fair', color: '#eab308' },
            { score: 4, label: 'Strong', color: '#22c55e' },
            { score: 5, label: 'Very Strong', color: '#10b981' },
        ];
        return levels[score] || levels[0];
    };

    const passwordStrength = getPasswordStrength(password);

    const isFormValid = () => {
        return (
            contactName.trim() &&
            contactEmail.trim() &&
            password.length >= 8 &&
            /[A-Z]/.test(password) &&
            /[a-z]/.test(password) &&
            /\d/.test(password) &&
            password === confirmPassword &&
            termsAccepted
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        if (password !== confirmPassword) {
            setFormError('Passwords do not match.');
            return;
        }

        if (!isFormValid()) {
            setFormError('Please fill in all required fields and accept the terms.');
            return;
        }

        if (preferences && !preferences.payment) {
            setFormError('Payment cookies are required to process payments securely via Stripe. Please update your cookie preferences.');
            return;
        }

        try {
            setSubmitting(true);

            // Step 1: Save setup data
            await axios.post(`${API_BASE_URL}/activate/${token}/setup`, {
                contactName,
                contactEmail,
                phone,
                companyWebsite,
                password,
            });

            // Step 2: Create Stripe Checkout session and redirect
            const checkoutRes = await axios.post(`${API_BASE_URL}/activate/${token}/checkout`);

            if (checkoutRes.data.url) {
                window.location.href = checkoutRes.data.url;
            } else {
                setFormError('Failed to create payment session. Please try again.');
            }
        } catch (err) {
            setFormError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="activation-page">
                <div className="activation-container">
                    <div className="activation-loading">
                        <div className="activation-spinner" />
                        <p>Loading your activation details...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error states
    if (error) {
        return (
            <div className="activation-page">
                <div className="activation-container">
                    <div className="activation-header">
                        <div className="activation-logo">
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                                <rect width="40" height="40" rx="10" fill="#6366f1" />
                                <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fill="white" fontSize="18" fontWeight="700">E</text>
                            </svg>
                            <span className="activation-logo-text">Envion</span>
                        </div>
                    </div>
                    <div className="activation-error-card">
                        <div className="activation-error-icon">
                            {error.type === 'expired' ? '⏰' : error.type === 'used' ? '✅' : '❌'}
                        </div>
                        <h2>{error.type === 'used' ? 'Already Activated' : 'Link Not Valid'}</h2>
                        <p>{error.message}</p>
                        {error.type === 'used' && (
                            <button className="activation-btn-primary" onClick={() => navigate('/login')}>
                                Go to Login
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Main activation form
    return (
        <div className="activation-page">
            <div className="activation-container">
                {/* Header */}
                <div className="activation-header">
                    <div className="activation-logo">
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <rect width="40" height="40" rx="10" fill="#6366f1" />
                            <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fill="white" fontSize="18" fontWeight="700">E</text>
                        </svg>
                        <span className="activation-logo-text">Envion</span>
                    </div>
                    <h1>Welcome to Envion</h1>
                    <p className="activation-subtitle">Set up your account and get started</p>
                </div>

                {/* Payment cancelled notice */}
                {paymentCancelled && (
                    <div className="activation-notice activation-notice-warning">
                        <span className="activation-notice-icon">⚠️</span>
                        <p>Payment was not completed. Please try again when you're ready.</p>
                    </div>
                )}

                {/* Plan Summary */}
                <div className="activation-plan-card">
                    <h3>Your Plan</h3>
                    <div className="activation-plan-details">
                        <div className="activation-plan-row">
                            <span className="activation-plan-label">Company</span>
                            <span className="activation-plan-value">{linkData.companyName}</span>
                        </div>
                        <div className="activation-plan-row">
                            <span className="activation-plan-label">Plan</span>
                            <span className="activation-plan-value">
                                {linkData.billingCycle === 'annual' ? 'Annual' : 'Monthly'} Billing
                            </span>
                        </div>
                        <div className="activation-plan-row activation-plan-price">
                            <span className="activation-plan-label">Price</span>
                            <span className="activation-plan-value">
                                {linkData.billingCycle === 'annual'
                                    ? `£${linkData.annualPrice?.toLocaleString()}/year`
                                    : `£${linkData.monthlyPrice?.toLocaleString()}/month`
                                }
                            </span>
                        </div>
                        <div className="activation-plan-row">
                            <span className="activation-plan-label">Includes</span>
                            <span className="activation-plan-value">
                                Up to {linkData.maxActiveAdmins} admin users, up to {linkData.maxOnboardersMonth} onboardings/month
                            </span>
                        </div>
                    </div>
                </div>

                {/* Account Setup Form */}
                <form onSubmit={handleSubmit} className="activation-form">
                    <h3>Account Setup</h3>

                    {/* Read-only fields */}
                    <div className="activation-form-group">
                        <label>Company Name</label>
                        <input type="text" value={linkData.companyName} disabled className="activation-input-disabled" />
                    </div>

                    <div className="activation-form-group">
                        <label>Company Email</label>
                        <input type="email" value={linkData.companyEmail} disabled className="activation-input-disabled" />
                    </div>

                    {/* Editable fields */}
                    <div className="activation-form-row">
                        <div className="activation-form-group">
                            <label>Contact Name <span className="required">*</span></label>
                            <input
                                type="text"
                                value={contactName}
                                onChange={(e) => setContactName(e.target.value)}
                                placeholder="Your full name"
                                required
                            />
                        </div>
                        <div className="activation-form-group">
                            <label>Contact Email <span className="required">*</span></label>
                            <input
                                type="email"
                                value={contactEmail}
                                onChange={(e) => setContactEmail(e.target.value)}
                                placeholder="your@email.com"
                                required
                            />
                        </div>
                    </div>

                    <div className="activation-form-row">
                        <div className="activation-form-group">
                            <label>Phone</label>
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+44 ..."
                            />
                        </div>
                        <div className="activation-form-group">
                            <label>Company Website</label>
                            <input
                                type="url"
                                value={companyWebsite}
                                onChange={(e) => setCompanyWebsite(e.target.value)}
                                placeholder="https://..."
                            />
                        </div>
                    </div>

                    <div className="activation-form-divider" />

                    <h4>Create Your Password</h4>

                    <div className="activation-form-row">
                        <div className="activation-form-group">
                            <label>Password <span className="required">*</span></label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Minimum 8 characters"
                                required
                            />
                            {password && (
                                <div className="activation-password-strength">
                                    <div className="activation-strength-bar">
                                        <div
                                            className="activation-strength-fill"
                                            style={{
                                                width: `${(passwordStrength.score / 5) * 100}%`,
                                                backgroundColor: passwordStrength.color,
                                            }}
                                        />
                                    </div>
                                    <span style={{ color: passwordStrength.color, fontSize: '12px' }}>
                                        {passwordStrength.label}
                                    </span>
                                </div>
                            )}
                            <ul className="activation-password-rules">
                                <li className={password.length >= 8 ? 'met' : ''}>At least 8 characters</li>
                                <li className={/[A-Z]/.test(password) ? 'met' : ''}>One uppercase letter</li>
                                <li className={/[a-z]/.test(password) ? 'met' : ''}>One lowercase letter</li>
                                <li className={/\d/.test(password) ? 'met' : ''}>One number</li>
                            </ul>
                        </div>
                        <div className="activation-form-group">
                            <label>Confirm Password <span className="required">*</span></label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter your password"
                                required
                            />
                            {confirmPassword && password !== confirmPassword && (
                                <span className="activation-field-error">Passwords do not match</span>
                            )}
                        </div>
                    </div>

                    <div className="activation-form-divider" />

                    {/* Terms */}
                    <div className="activation-terms">
                        <label className="activation-checkbox-label">
                            <input
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={(e) => setTermsAccepted(e.target.checked)}
                            />
                            <span>
                                I agree to the{' '}
                                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
                                and{' '}
                                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                            </span>
                        </label>
                    </div>

                    {formError && (
                        <div className="activation-notice activation-notice-error">
                            <span className="activation-notice-icon">❌</span>
                            <p>{formError}</p>
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        className="activation-btn-primary activation-btn-lg"
                        disabled={!isFormValid() || submitting}
                    >
                        {submitting ? (
                            <>
                                <span className="activation-btn-spinner" />
                                Processing...
                            </>
                        ) : (
                            <>
                                🔒 Proceed to Payment
                            </>
                        )}
                    </button>

                    <p className="activation-secure-note">
                        Payments are securely processed by Stripe. Your card details are never stored on our servers.
                    </p>

                    {preferences && !preferences.payment && (
                        <div style={{ textAlign: 'center', marginTop: '16px' }}>
                            <button
                                type="button"
                                onClick={() => setShowBanner(true)}
                                style={{ background: 'none', border: 'none', color: '#6366f1', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.875rem' }}
                            >
                                Update Cookie Preferences
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ActivationPage;
